import { z } from 'zod';

/**
 * Travel (brief section 7.3, docs/prototype.html): what a team member
 * adds to a show (flights, hotels, car rentals, trains, badges, plans and
 * documents, anything else), what is missing, and the show logistics kept
 * by the admins. Shared by the API (checks) and the screens.
 */

export const TRAVEL_TYPES = [
  'flight',
  'hotel',
  'car',
  'train',
  'badge',
  'document',
  'other',
] as const;
export type TravelType = (typeof TRAVEL_TYPES)[number];

export const TRAVEL_TYPE_LABELS: Record<TravelType, string> = {
  flight: 'Flight',
  hotel: 'Hotel',
  car: 'Car rental',
  train: 'Train',
  badge: 'Badge',
  document: 'Plan or document',
  other: 'Other',
};

/** Badges and documents: a file, and they can be for everyone on the team. */
export const FILE_TYPES: readonly TravelType[] = ['badge', 'document'];
export const isFileType = (type: unknown) => type === 'badge' || type === 'document';

/** A file attached to an item or to the show logistics: a PDF or a picture, 15 MB at most. */
export const TRAVEL_FILE_MAX_BYTES = 15 * 1024 * 1024;
export const TRAVEL_FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

const text = (max = 200) => z.string().trim().max(max).default('');
/** A local date and time as the confirmation gives it ("2026-10-25T18:30"), without a time zone. */
const when = z
  .string()
  .trim()
  .regex(/^(\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?)?$/)
  .default('');
const date = z
  .string()
  .trim()
  .regex(/^(\d{4}-\d{2}-\d{2})?$/)
  .default('');
const time = z
  .string()
  .trim()
  .regex(/^(\d{2}:\d{2})?$/)
  .default('');

/** Each type's own fields; the reference and the link are common to all. */
export const TRAVEL_FIELDS = {
  flight: z.object({
    dir: z.enum(['out', 'back']).default('out'),
    number: text(20),
    carrier: text(80),
    from: text(80),
    to: text(80),
    depart: when,
    arrive: when,
    seat: text(10),
    checkin: text(500),
  }),
  hotel: z.object({
    name: text(),
    address: text(300),
    check_in: date,
    check_out: date,
    check_in_time: time,
    check_out_time: time,
    to_venue: text(),
  }),
  car: z.object({
    company: text(80),
    pickup: text(),
    pickup_at: when,
    return_at: when,
  }),
  train: z.object({
    // Nov 2, 2026: the outbound and the return train of a round trip, each with its number.
    dir: z.enum(['out', 'back']).default('out'),
    number: text(20),
    operator: text(80),
    from: text(80),
    to: text(80),
    depart: when,
    arrive: when,
    seat: text(20),
  }),
  badge: z.object({ title: text() }),
  document: z.object({
    title: text(),
    kind: z.enum(['plan', 'doc']).default('plan'),
    detail: text(500),
  }),
  other: z.object({ title: text(), detail: text(500), when }),
} as const;

export type TravelFields = Record<string, string>;

/** What a person sends to add or change an item. */
export interface TravelItemInput {
  type: TravelType;
  fields: TravelFields;
  reference: string;
  link: string;
  travelers: string[];
  everyone: boolean;
}

/** What is needed per type (brief section 14, group K), in the prototype's words. */
const REQUIRED: Record<
  TravelType,
  { ok(f: TravelFields, hasFile: boolean): boolean; message: string }
> = {
  flight: {
    ok: (f) => !!(f.number && f.depart),
    message: 'Flight number and departure are needed.',
  },
  hotel: {
    ok: (f) => !!(f.name && f.check_in && f.check_out),
    message: 'Hotel, check-in and check-out are needed.',
  },
  car: {
    ok: (f) => !!(f.company && f.pickup_at),
    message: 'Rental company and pick-up are needed.',
  },
  train: {
    ok: (f) => !!(f.from && f.to && f.depart),
    message: 'From, to and departure are needed.',
  },
  badge: { ok: (f, file) => !!(f.title || file), message: 'Add a title or a file.' },
  document: { ok: (f, file) => !!(f.title && file), message: 'Add a title and a file.' },
  other: { ok: (f) => !!f.title, message: 'Say what it is.' },
};

const itemSchema = z.object({
  type: z.enum(TRAVEL_TYPES),
  fields: z.record(z.string(), z.unknown()).default({}),
  reference: z.string().trim().max(80).default(''),
  link: z
    .string()
    .trim()
    .max(500)
    .refine((v) => v === '' || /^https?:\/\/\S+$/i.test(v))
    .default(''),
  travelers: z.array(z.uuid()).max(50).default([]),
  everyone: z.boolean().default(false),
});

export type TravelCheck = { ok: true; value: TravelItemInput } | { ok: false; message: string };

/**
 * Checks an item: its type's fields, what is needed, and who it is for
 * (at least one traveler; "everyone on the team" only for badges and
 * documents). `hasFile` says whether the item has (or gets) its file.
 */
export function checkTravelItem(input: unknown, hasFile = false): TravelCheck {
  const parsed = itemSchema.safeParse(input);
  if (!parsed.success) return { ok: false, message: 'Check the fields.' };
  const { type, reference, link, everyone } = parsed.data;
  const fields = TRAVEL_FIELDS[type].safeParse(parsed.data.fields);
  if (!fields.success) return { ok: false, message: 'Check the dates and times.' };
  const f = fields.data as TravelFields;
  if (!REQUIRED[type].ok(f, hasFile)) return { ok: false, message: REQUIRED[type].message };
  const forAll = isFileType(type) && everyone;
  const travelers = [...new Set(parsed.data.travelers)];
  if (!forAll && travelers.length === 0) return { ok: false, message: 'Choose who it is for.' };
  return {
    ok: true,
    value: {
      type,
      fields: f,
      reference,
      link,
      travelers: forAll ? [] : travelers,
      everyone: forAll,
    },
  };
}

/** An item as the screens get it. */
export interface TravelItem {
  id: string;
  event_id: string;
  type: TravelType;
  fields: TravelFields;
  reference: string | null;
  link: string | null;
  travelers: string[];
  everyone: boolean;
  file_name: string | null;
  file_type: string | null;
  has_file: boolean;
  added_by: string;
  added_by_name: string;
  updated_at: string;
}

/** Is this item for this person (a traveler, or everyone on the team)? */
export const itemFor = (item: Pick<TravelItem, 'travelers' | 'everyone'>, userId: string) =>
  item.everyone || item.travelers.includes(userId);

/** When an item starts, to sort them: departure, pick-up, check-in or "when". */
export function itemStart(item: Pick<TravelItem, 'fields'>): string {
  const f = item.fields;
  return f.depart || f.pickup_at || f.check_in || f.when || '';
}

const ORDER: TravelType[] = ['flight', 'train', 'car', 'hotel', 'other', 'badge', 'document'];
/** Flights, trains, car rentals, hotels, the rest; each by when it starts. */
export function sortItems<T extends Pick<TravelItem, 'type' | 'fields'>>(items: readonly T[]): T[] {
  return [...items].sort(
    (a, b) =>
      ORDER.indexOf(a.type) - ORDER.indexOf(b.type) || itemStart(a).localeCompare(itemStart(b)),
  );
}

/** An item's name on a card, in the team view and in a calendar. */
export function itemTitle(item: Pick<TravelItem, 'type' | 'fields'>): string {
  const f = item.fields;
  switch (item.type) {
    case 'flight':
      return [f.carrier, f.number].filter(Boolean).join(' ');
    case 'hotel':
      return f.name ?? '';
    case 'car':
      return f.company ?? '';
    case 'train':
      return [f.operator, f.number].filter(Boolean).join(' ') || 'Train';
    default:
      return f.title || TRAVEL_TYPE_LABELS[item.type];
  }
}

/** What is missing for a person: outbound flight, return flight, hotel (prototype: tripGaps). */
export function tripGaps(
  items: readonly Pick<TravelItem, 'type' | 'fields' | 'travelers' | 'everyone'>[],
  userId: string,
): string[] {
  const mine = items.filter((i) => itemFor(i, userId));
  const gaps: string[] = [];
  if (!mine.some((i) => i.type === 'flight' && i.fields.dir !== 'back'))
    gaps.push('Outbound flight missing');
  if (!mine.some((i) => i.type === 'flight' && i.fields.dir === 'back'))
    gaps.push('Return flight missing');
  if (!mine.some((i) => i.type === 'hotel')) gaps.push('Hotel missing');
  return gaps;
}

/** The person's outbound flight (the first one), for the trip card and the team. */
export function outboundFlight<
  T extends Pick<TravelItem, 'type' | 'fields' | 'travelers' | 'everyone'>,
>(items: readonly T[], userId: string): T | undefined {
  return sortItems(
    items.filter((i) => itemFor(i, userId) && i.type === 'flight' && i.fields.dir !== 'back'),
  )[0];
}

/** The show logistics' fields (brief section 7.3), in the prototype's order. */
export const LOGISTICS_FIELDS = [
  ['venue', 'Venue'],
  ['address', 'Address'],
  ['hall', 'Hall'],
  ['booth', 'Booth'],
  ['entrance', 'Exhibitor entrance'],
  ['parking', 'Parking'],
  ['site', 'Show website'],
  ['badges', 'Badges'],
  ['setup', 'Setup'],
  ['teardown', 'Teardown'],
  ['shipment', 'Shipment'],
  ['builder', 'Booth builder'],
  ['wifi', 'Wi-Fi'],
  ['organizer', 'Show organizer'],
  ['emergency', 'Emergency'],
  ['dinner', 'Team dinner'],
  ['notes', 'Notes'],
] as const;
export type LogisticsField = (typeof LOGISTICS_FIELDS)[number][0];
export const LOGISTICS_KEYS = LOGISTICS_FIELDS.map(([k]) => k) as LogisticsField[];

/** Copied from the previous edition of the same show (marked "Suggested, check it"). */
export const COPIED_FIELDS: readonly LogisticsField[] = [
  'venue',
  'address',
  'hall',
  'entrance',
  'parking',
  'site',
  'organizer',
  'builder',
  'wifi',
  'emergency',
];

export interface OpeningDay {
  date: string;
  open: string;
  close: string;
  label?: string;
}

export interface Logistics {
  fields: Partial<Record<LogisticsField, string>>;
  /** Fields filled by "Copy from last edition" or the exhibitor manual, to check. */
  suggested: LogisticsField[];
  hours: OpeningDay[];
  hours_confirmed: boolean;
}

export const logisticsSchema = z.object({
  fields: z
    .record(z.string(), z.string().trim().max(1000))
    .default({})
    .transform((f) =>
      Object.fromEntries(
        Object.entries(f).filter(([k, v]) => (LOGISTICS_KEYS as string[]).includes(k) && v !== ''),
      ),
    ),
  suggested: z
    .array(z.string())
    .default([])
    .transform((s) => [...new Set(s.filter((k) => (LOGISTICS_KEYS as string[]).includes(k)))]),
  hours: z
    .array(
      z.object({
        date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
        open: z.string().regex(/^\d{2}:\d{2}$/),
        close: z.string().regex(/^\d{2}:\d{2}$/),
        label: z.string().trim().max(80).optional(),
      }),
    )
    .max(31)
    .default([])
    .transform((h) => [...h].sort((a, b) => a.date.localeCompare(b.date))),
  hours_confirmed: z.boolean().default(false),
  team: z.array(z.uuid()).max(100).optional(),
});

const shiftDay = (iso: string, days: number) => {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

/**
 * Opening hours from the show's dates (prototype: genHours): the day
 * before for the setup (07:00 to 20:00, exhibitors only), then each day
 * 09:00 to 18:00, the last day closing at 16:00. Edited per day after.
 */
export function generateHours(startsOn: string | null, endsOn: string | null): OpeningDay[] {
  if (!startsOn) return [];
  const start = startsOn.slice(0, 10);
  const end = (endsOn ?? startsOn).slice(0, 10) < start ? start : (endsOn ?? startsOn).slice(0, 10);
  const days: OpeningDay[] = [
    { date: shiftDay(start, -1), open: '07:00', close: '20:00', label: 'Setup, exhibitors only' },
  ];
  for (let d = start; d <= end && days.length < 31; d = shiftDay(d, 1)) {
    days.push({ date: d, open: '09:00', close: d === end ? '16:00' : '18:00' });
  }
  return days;
}

/** What the show logistics still lack (prototype: showGaps). */
export function showGaps(
  l: Pick<Logistics, 'fields' | 'hours' | 'hours_confirmed'> | null,
): string[] {
  const gaps: string[] = [];
  if (!l?.hours.length) gaps.push('Opening hours missing');
  else if (!l.hours_confirmed) gaps.push('Opening hours not confirmed');
  if (!l?.fields.booth) gaps.push('Booth number missing');
  return gaps;
}

/** Whole days from today to a date (positive: still to come). */
export function daysTo(date: string | null, today: string): number | null {
  if (!date) return null;
  const ms =
    Date.parse(`${date.slice(0, 10)}T12:00:00Z`) - Date.parse(`${today.slice(0, 10)}T12:00:00Z`);
  return Math.round(ms / 86_400_000);
}

const icsText = (v: string) => v.replace(/([\\;,])/g, '\\$1').replace(/\r?\n/g, '\\n');
const icsTime = (v: string) => `${v.replace(/[-:]/g, '').slice(0, 13).padEnd(13, '0')}00`;

/**
 * Add to calendar (.ics) for an item, in the local time of its
 * confirmation (prototype: icsFor). Badges and documents have none.
 */
export function travelIcs(
  item: Pick<TravelItem, 'id' | 'type' | 'fields' | 'reference'>,
  eventName: string,
): string | null {
  const f = (key: string) => item.fields[key] ?? '';
  const route = `${itemTitle(item)} ${f('from')} to ${f('to')}`;
  const parts: Partial<Record<TravelType, [string, string, string, string]>> = {
    flight: [f('depart'), f('arrive'), route, f('from')],
    train: [f('depart'), f('arrive'), route, f('from')],
    hotel: [
      f('check_in') && `${f('check_in')}T${f('check_in_time') || '15:00'}`,
      f('check_out') && `${f('check_out')}T${f('check_out_time') || '11:00'}`,
      `Hotel: ${f('name')}`,
      f('address'),
    ],
    car: [f('pickup_at'), f('return_at'), `Car rental: ${f('company')}`, f('pickup')],
    other: [f('when'), f('when'), f('title'), f('detail')],
  };
  const [start, end, title, place] = parts[item.type] ?? ['', '', '', ''];
  if (!start) return null;
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Waste Robotics//Sales Portal//EN',
    'BEGIN:VEVENT',
    `UID:${item.id}@portal.wasterobotic.com`,
    `DTSTART:${icsTime(start)}`,
    `DTEND:${icsTime(end || start)}`,
    `SUMMARY:${icsText(`${title} (${eventName})`)}`,
    `LOCATION:${icsText(place ?? '')}`,
    `DESCRIPTION:${icsText(`Ref ${item.reference || '-'}`)}`,
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].join('\r\n');
}

/* ---------- Reading (part b): confirmations and exhibitor manuals ---------- */

/** One booking read in a confirmation: a flight, a hotel, a car... */
export interface ConfirmationItem {
  type: TravelType;
  fields: TravelFields;
  reference: string;
  link: string;
}

/**
 * What the AI read in a confirmation, checked: a draft the person confirms.
 * The type, fields, reference and link are its first booking's.
 */
export interface ConfirmationReading {
  type: TravelType | null;
  fields: TravelFields;
  reference: string;
  link: string;
  /**
   * Round 2: every booking the document holds (outbound and return
   * flights, connections, a hotel, a car), in its order. Absent on a
   * reading made before.
   */
  items?: ConfirmationItem[];
  /** The passenger or guest names printed on it. */
  passengers: string[];
  /** Those names that match people on the team. */
  travelers: string[];
}

/** Every booking of a reading: its items, or its one booking (a reading made before round 2). */
export function readingItems(
  reading: Pick<ConfirmationReading, 'type' | 'fields' | 'reference' | 'link' | 'items'>,
): ConfirmationItem[] {
  if (reading.items?.length) return reading.items;
  const { type, fields, reference, link } = reading;
  return type ? [{ type, fields, reference, link }] : [];
}

/** "KL 672" and "kl672" are the same flight. */
const flightKey = (number: string | undefined) => (number ?? '').replace(/\s+/g, '').toUpperCase();

/**
 * Two items are the same booking (round 2): the same type and reference
 * and, for a flight, the same flight number, so a return is never taken
 * for a duplicate of its outbound flight. Nov 2, 2026: a train too, by its
 * number and its departure (a round trip's two trains share the reference).
 */
export function sameBooking(
  a: { type: TravelType; fields: TravelFields; reference: string | null },
  b: { type: TravelType; fields: TravelFields; reference: string | null },
): boolean {
  if (a.type !== b.type || !a.reference || !b.reference) return false;
  if (a.reference.trim().toLowerCase() !== b.reference.trim().toLowerCase()) return false;
  if (a.type === 'train') {
    return (
      flightKey(a.fields.number) === flightKey(b.fields.number) &&
      (a.fields.depart ?? '') === (b.fields.depart ?? '')
    );
  }
  return a.type !== 'flight' || flightKey(a.fields.number) === flightKey(b.fields.number);
}

/** What the AI read in an exhibitor manual: suggested logistics, to check. */
export interface ManualReading {
  fields: Partial<Record<LogisticsField, string>>;
  hours: OpeningDay[];
}

export interface TravelDraft {
  id: string;
  event_id: string;
  source: 'upload' | 'email' | 'manual';
  type: TravelType | null;
  status: 'waiting' | 'ready' | 'failed';
  file_name: string | null;
  subject: string | null;
  url: string | null;
  created_at: string;
  result: ConfirmationReading | ManualReading | null;
  /** Round 2: why the reading failed (absent before). */
  failure?: TravelReadFailureReason | null;
}

export type TravelReadFailureReason =
  'too_large' | 'format' | 'unavailable' | 'unreadable' | 'missing';

/** Why Claude could not read a confirmation or a manual, in one sentence (round 2). */
export const TRAVEL_READ_FAILURES: Record<TravelReadFailureReason, string> = {
  too_large: 'The file is too large for Claude (a PDF of 24 MB at most).',
  format: 'Claude does not read this file: send a PDF, or a JPEG or PNG picture.',
  unavailable: 'Claude is not available right now.',
  unreadable: 'Claude found no booking it could read in it.',
  missing: 'The file did not reach the portal: send it again.',
};

/** "Claude could not read it" with its reason, then what to do. */
export function travelReadFailureText(reason: TravelReadFailureReason | null | undefined): string {
  return reason
    ? `Claude could not read it. ${TRAVEL_READ_FAILURES[reason]} Type it instead.`
    : 'Claude could not read it. Type it instead.';
}

/** The names of a reading's fields: the AI's keys, then the portal's. */
const READ_KEYS: Record<string, string> = {
  checkin_link: 'checkin',
  hotel_name: 'name',
  check_in_date: 'check_in',
  check_out_date: 'check_out',
};

/** "2026-10-25 18:40" or "2026-10-25T18:40:00" as the form takes it: "2026-10-25T18:40". */
const asLocalTime = (v: string) =>
  /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(v) ? `${v.slice(0, 10)}T${v.slice(11, 16)}` : v;

const cleanText = (v: unknown, max = 300) =>
  typeof v === 'string' && v.trim() ? v.trim().slice(0, max) : '';

/** One booking as the AI gave it, kept only for what fits its type's fields. */
function normalizeItem(
  raw: Record<string, unknown>,
  type: TravelType | null,
  reference: string,
): Omit<ConfirmationItem, 'type'> & { type: TravelType | null } {
  const fields: TravelFields = {};
  if (type) {
    const shape = TRAVEL_FIELDS[type].shape as Record<string, z.ZodType>;
    for (const [key, value] of Object.entries(raw)) {
      const name = READ_KEYS[key] ?? key;
      if (!(name in shape) || typeof value !== 'string' || !value.trim()) continue;
      const one = shape[name]!.safeParse(asLocalTime(value.trim()).slice(0, 300));
      if (one.success && typeof one.data === 'string' && one.data) fields[name] = one.data;
    }
  }
  const link = cleanText(raw.link, 500);
  return {
    type,
    fields,
    reference: cleanText(raw.reference, 80) || reference,
    link: /^https?:\/\/\S+$/i.test(link) ? link : '',
  };
}

const readType = (v: unknown) =>
  TRAVEL_TYPES.includes(v as TravelType) ? (v as TravelType) : null;

/**
 * Keeps only what fits the type's fields: a date, a time or a link that
 * does not look right is left empty (the person types it), never guessed.
 * Round 2: every booking of the document (`items`), each of its own type
 * (the type the person chose only when the AI gives none), with the
 * document's reference when it has none of its own.
 */
export function normalizeConfirmation(
  raw: Record<string, unknown>,
  hint: TravelType | null = null,
): Omit<ConfirmationReading, 'travelers'> {
  const passengers = Array.isArray(raw.passengers)
    ? raw.passengers
        .map((p) => cleanText(p, 120))
        .filter(Boolean)
        .slice(0, 20)
    : [];
  const shared = cleanText(raw.reference, 80);
  if (Array.isArray(raw.items)) {
    const items = raw.items
      .filter(
        (i): i is Record<string, unknown> =>
          typeof i === 'object' && i !== null && !Array.isArray(i),
      )
      .slice(0, 20)
      .map((i) => normalizeItem(i, readType(i.type) ?? hint, shared))
      .filter((i): i is ConfirmationItem => i.type !== null && Object.keys(i.fields).length > 0);
    const first = items[0];
    return {
      type: first?.type ?? hint,
      fields: first?.fields ?? {},
      reference: first?.reference ?? shared,
      link: first?.link ?? '',
      items,
      passengers,
    };
  }
  // A reading of one booking (before round 2).
  const one = normalizeItem(raw, hint ?? readType(raw.type), '');
  return { ...one, passengers };
}

export function normalizeManual(raw: Record<string, unknown>): ManualReading {
  const given = (raw.fields ?? {}) as Record<string, unknown>;
  const fields: ManualReading['fields'] = {};
  for (const key of LOGISTICS_KEYS) {
    const value = cleanText(given[key], 1000);
    if (value) fields[key] = value;
  }
  const hours = logisticsSchema.shape.hours.safeParse(
    Array.isArray(raw.hours)
      ? raw.hours.filter(
          (h): h is Record<string, unknown> =>
            typeof h === 'object' && h !== null && !Array.isArray(h),
        )
      : [],
  );
  return { fields, hours: hours.success ? hours.data : [] };
}

/** "Chloé TREMBLAY" and "TREMBLAY/CHLOE MRS" are Chloé Tremblay: accents, case and titles aside. */
const nameWords = (name: string) =>
  name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter(
      (w) =>
        w.length > 1 && !['mr', 'mrs', 'ms', 'miss', 'dr', 'mme', 'm', 'herr', 'frau'].includes(w),
    );

/** The team members whose first and last names are both in a printed name. */
export function matchTravelers(
  names: readonly string[],
  team: readonly { id: string; name: string }[],
): string[] {
  const found = new Set<string>();
  for (const printed of names) {
    const words = new Set(nameWords(printed));
    for (const member of team) {
      const parts = nameWords(member.name);
      if (parts.length >= 2 && parts.every((p) => words.has(p))) found.add(member.id);
    }
  }
  return [...found];
}

/**
 * The address to forward a show's confirmations to: the inbound address
 * with the event ID after a plus (trips+ifat-2026@trips.example.com).
 */
export function inboundAddress(base: string | null | undefined, eventCode: string): string | null {
  const at = base?.indexOf('@') ?? -1;
  if (!base || at < 1) return null;
  return `${base.slice(0, at)}+${eventCode.toLowerCase()}${base.slice(at)}`;
}

/** The event ID in a forwarded email's address (null when there is none). */
export function inboundEventCode(address: string): string | null {
  const match = /\+([a-z0-9-]+)@/i.exec(address);
  return match ? match[1]!.toUpperCase() : null;
}
